import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Button } from "@/components/ui/button";
import { FilterIcon, PlusIcon } from "lucide-react";

interface ChapterActivitiesFiltersProps {
  filters: {
    searchTerm: string;
    filter: "my" | "chapter";
  };
  onFilterChange: (filterKey: string, value: string) => void;
  onCreateNew?: () => void;
}

const ChapterActivitiesFilters: React.FC<ChapterActivitiesFiltersProps> = ({
  filters,
  onFilterChange,
  onCreateNew,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.searchTerm) {
      onFilterChange("searchTerm", debouncedSearch);
    }
  }, [debouncedSearch, filters.searchTerm, onFilterChange]);

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
        <div className="w-full md:w-1/2">
          <SearchInput
            onChange={setSearchInput}
            placeholder="Search by activity name or description..."
            value={searchInput}
          />
        </div>

        {onCreateNew && (
          <Button className="shrink-0" onClick={onCreateNew}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Submit New Activity
          </Button>
        )}
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <FilterIcon className="h-4 w-4 mr-2 text-gray-500" />
          <span className="text-sm font-medium mr-3">View:</span>
        </div>

        <div className="flex space-x-2">
          <Button
            className="text-sm"
            onClick={() => onFilterChange("filter", "my")}
            size="sm"
            variant={filters.filter === "my" ? "default" : "outline"}
          >
            My Activities
          </Button>

          <Button
            className="text-sm"
            onClick={() => onFilterChange("filter", "chapter")}
            size="sm"
            variant={filters.filter === "chapter" ? "default" : "outline"}
          >
            Chapter Activities
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChapterActivitiesFilters;
